import { PaymentModuleId } from '@foundation/apis/payments';
import { SupportedLanguage } from '@foundation/localization';
import { useAuth } from '@shared/auth';
import { useTranslation } from '@shared/localization';
import { useEffect, useState, JSX } from 'react';

import { LoadingSpinner, LoadingSpinnerSize } from '@/components/common';
import { AlertWarning } from '@/components/common/alerts';
import { PayButton } from '@/components/pages/stored-instruments/utils/PayButton';
import { PaymentIntentFlow } from '@/schemas/PaymentIntent';
import FlutterwaveCardService from '@/services/flutterwave/FlutterwaveCardService';
import Logger, { LoggerOrigin } from '@/services/Logger';
import PaymentStore from '@/services/payments/PaymentStore';
import TelemetryService from '@/services/telemetry/TelemetryService';
import { redirect } from '@/utils/redirect';

interface IPayWithFlutterwaveCardBtnProps {
  navigation?: JSX.Element | null;
}

export function PayWithFlutterwaveCardBtn({
  navigation,
}: IPayWithFlutterwaveCardBtnProps) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Error>();
  const { accessToken, userId } = useAuth();

  useEffect(() => {
    setLoading(true);
    setError(undefined);

    let promise;
    if (PaymentStore.flow === PaymentIntentFlow.Manage) {
      const customerNumber = userId;
      if (customerNumber === undefined) {
        throw new Error('customerNumber is undefined!');
      }

      if (PaymentStore.legalEntity === undefined) {
        throw new Error('Legal entity cannot be undefined');
      }

      promise = FlutterwaveCardService.getManageRedirectUrl(
        PaymentStore.legalEntity,
        customerNumber,
        accessToken ?? '',
        PaymentModuleId.FlutterwaveCreditCard,
        language as SupportedLanguage,
      );
    } else {
      promise = FlutterwaveCardService.getRedirectUrl(
        language as SupportedLanguage,
        accessToken ?? '',
      );
    }

    promise
      .then(setRedirectUrl)
      .catch((e: Error) => {
        TelemetryService.trackException(e, 'FlutterwaveCardBtn');
        Logger.error(
          LoggerOrigin.ReactComponent,
          'Flutterwave URL fetch failure',
          e,
        );
        setError(e);
      })
      .finally(() => setLoading(false));
  }, [accessToken, language, userId]);

  if (error) {
    return (
      <AlertWarning
        title={t('Something went wrong!')}
        description={t('Card payments are currently unavailable.')}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="h-14">
        <LoadingSpinner size={LoadingSpinnerSize.Small} />
      </div>
    );
  }

  return (
    <div className="flex space-x-2">
      {navigation}
      <PayButton
        onClick={() => redirect(redirectUrl)}
        title={
          PaymentStore.flow === PaymentIntentFlow.Manage
            ? t('Add new card')
            : t('Proceed to pay')
        }
      />
    </div>
  );
}
